import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, useMotionValue, useTransform, useInView, animate, type Variants } from 'framer-motion'
import Map from 'ol/Map'
import View from 'ol/View'
import TileLayer from 'ol/layer/Tile'
import XYZ from 'ol/source/XYZ'
import { ESGLedgerLogo } from '../components/ESGLedgerLogo'
import { fromLonLat } from 'ol/proj'
import { defaults as defaultControls } from 'ol/control'
import { defaults as defaultInteractions } from 'ol/interaction'
import 'ol/ol.css'
import {
  GlobeAsiaAustraliaIcon,
  ShieldCheckIcon,
  CubeTransparentIcon,
  ChartBarIcon,
  ArrowRightIcon,
  BuildingLibraryIcon,
  ArchiveBoxArrowDownIcon,
  DocumentMagnifyingGlassIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'

const CSDIHeroMap: React.FC = () => {
  const mapElement = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!mapElement.current) return

    // CSDI Topographic Base Map
    const csdiLayer = new TileLayer({
      source: new XYZ({
        url: 'https://mapapi.geodata.gov.hk/gs/api/v1.0.0/xyz/basemap/wgs84/{z}/{x}/{y}.png',
        crossOrigin: 'anonymous',
        minZoom: 10,
        maxZoom: 20
      }),
      className: 'filter grayscale invert opacity-70 contrast-150'
    })

    const baseCenter = fromLonLat([114.155, 22.284]) // Central & Western District

    const map = new Map({
      target: mapElement.current,
      layers: [csdiLayer],
      controls: defaultControls({ attribution: false, zoom: false }),
      interactions: defaultInteractions({ mouseWheelZoom: false, dragPan: false }),
      view: new View({
        center: baseCenter,
        zoom: 16
      })
    })

    // Add LandsD / CSDI official attribution manually for Gov compliance
    const attributionStr = `
      <div style="position: absolute; bottom: 8px; right: 8px; font-size: 8px; color: rgba(255,255,255,0.5); pointer-events: none; text-align: right; z-index: 10;">
        <img src="https://api.hkmapservice.gov.hk/mapapi/landsdlogo.jpg" style="height: 15px; margin-bottom: 2px" alt="LandsD Logo" />
      </div>
    `
    mapElement.current.insertAdjacentHTML('beforeend', attributionStr)

    // Subtle panning animation for "live" feel: a slow bounded sway around
    // the base center so the view never drifts off the CSDI tile coverage.
    let reqId: number
    const SWAY_RADIUS = 120 // meters in projected units
    const animateMap = (time: number) => {
      const t = time / 1000
      map.getView().setCenter([
        baseCenter[0] + Math.sin(t * 0.15) * SWAY_RADIUS,
        baseCenter[1] + Math.cos(t * 0.1) * SWAY_RADIUS * 0.6
      ])
      reqId = requestAnimationFrame(animateMap)
    }
    reqId = requestAnimationFrame(animateMap)

    return () => {
      cancelAnimationFrame(reqId)
      map.setTarget(undefined)
    }
  }, [])

  return <div ref={mapElement} className="absolute inset-0 w-full h-full pointer-events-none" />
}

/** Animated number that counts up when scrolled into view. */
const Counter: React.FC<{ target: number; suffix?: string; prefix?: string; decimals?: number }> = ({
  target,
  suffix = '',
  prefix = '',
  decimals = 0
}) => {
  const ref = useRef<HTMLSpanElement>(null)
  const isInView = useInView(ref, { once: true, margin: '-60px' })
  const motionValue = useMotionValue(0)
  const rendered = useTransform(motionValue, (v) =>
    `${prefix}${v.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`
  )

  useEffect(() => {
    if (!isInView) return
    const controls = animate(motionValue, target, { duration: 1.8, ease: [0.16, 1, 0.3, 1] })
    return controls.stop
  }, [isInView, motionValue, target])

  return (
    <span ref={ref} style={{ fontVariantNumeric: 'tabular-nums' }}>
      <motion.span>{rendered}</motion.span>
    </span>
  )
}

/** Scroll-triggered reveal wrapper. */
const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className
}) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
)

const TICKER_ITEMS = [
  'Credential Issuance',
  'Enterprise Grade',
  'ISO 27001',
  'Zero-Knowledge Proofs',
  'Smart Contracts',
  'Hyperledger Fabric',
  'AI-Powered Verification',
  'ESG Compliance',
  'Plastic Credits',
  'Immutable Audit Trails'
]

const BrandTicker: React.FC = () => (
  <div className="marquee border-y py-4 bg-white" style={{ borderColor: 'rgba(0, 119, 113, 0.12)' }}>
    <div className="marquee-track">
      {[0, 1].map((copy) => (
        <div key={copy} className="flex items-center shrink-0" aria-hidden={copy === 1}>
          {TICKER_ITEMS.map((item) => (
            <span key={`${copy}-${item}`} className="supplementary-type flex items-center whitespace-nowrap px-8">
              {item}
              <span className="ml-16 w-1.5 h-1.5 rounded-full bg-secondary-300 inline-block" />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
)

const TYPING_WORDS = [
  'verified value.',
  'digital trust.',
  'plastic credits.',
  'audit trails.',
  'real impact.'
]

/** Liftable-style typewriter: cycles through words after a fixed lead-in phrase. */
const TypewriterHeadline: React.FC = () => {
  const [wordIndex, setWordIndex] = useState(0)
  const [text, setText] = useState('')
  const [deleting, setDeleting] = useState(false)
  const pauseRef = useRef(false)

  useEffect(() => {
    const word = TYPING_WORDS[wordIndex]
    let delay = deleting ? 45 : 85

    if (pauseRef.current) {
      delay = 2000
      pauseRef.current = false
    }

    const timer = window.setTimeout(() => {
      if (!deleting) {
        if (text.length < word.length) {
          setText(word.slice(0, text.length + 1))
        } else {
          pauseRef.current = true
          setDeleting(true)
        }
      } else if (text.length > 0) {
        setText(word.slice(0, text.length - 1))
      } else {
        setDeleting(false)
        setWordIndex((i) => (i + 1) % TYPING_WORDS.length)
      }
    }, delay)

    return () => window.clearTimeout(timer)
  }, [text, deleting, wordIndex])

  return (
    <p className="mt-6 text-2xl md:text-4xl lg:text-5xl font-bold text-gray-800 leading-tight [text-shadow:0_2px_14px_rgba(255,255,255,0.9)]">
      <span className="block text-gray-700 font-semibold text-xl md:text-2xl lg:text-3xl mb-2">
        Where plastic waste becomes
      </span>
      <span className="text-primary-500">
        {text}
        <span className="inline-block w-[3px] h-[0.85em] ml-1 bg-primary-500 align-middle animate-pulse" aria-hidden="true" />
      </span>
    </p>
  )
}

/** Seamless looping hero: poster first, then dual-buffer swap (no native-loop seek hitch). */
const HERO_VIDEO_SRC = '/hero-bottles-small.mp4'
const HERO_POSTER_SRC = '/hero-bottles-poster.jpg'
const LOOP_LEAD_SEC = 0.18

const HeroLoopVideo: React.FC = () => {
  const videoA = useRef<HTMLVideoElement>(null)
  const videoB = useRef<HTMLVideoElement>(null)
  const frontRef = useRef<'a' | 'b'>('a')
  const swappingRef = useRef(false)
  const [front, setFront] = useState<'a' | 'b'>('a')
  const [hasFrame, setHasFrame] = useState(false)

  useEffect(() => {
    frontRef.current = front
  }, [front])

  useEffect(() => {
    const va = videoA.current
    const vb = videoB.current
    if (!va || !vb) return

    const arm = (video: HTMLVideoElement) => {
      video.muted = true
      video.defaultMuted = true
      video.playsInline = true
      video.loop = false
      video.setAttribute('muted', '')
      video.setAttribute('playsinline', '')
      video.setAttribute('webkit-playsinline', '')
    }

    const waitForFrame = (video: HTMLVideoElement) =>
      new Promise<void>((resolve) => {
        let done = false
        const finish = () => {
          if (done) return
          done = true
          resolve()
        }
        if ('requestVideoFrameCallback' in video) {
          ;(video as HTMLVideoElement & { requestVideoFrameCallback: (cb: () => void) => void })
            .requestVideoFrameCallback(() => finish())
        }
        requestAnimationFrame(() => requestAnimationFrame(() => finish()))
        window.setTimeout(finish, 120)
      })

    const safePlay = async (video: HTMLVideoElement) => {
      arm(video)
      try {
        await video.play()
      } catch {
        await new Promise<void>((resolve) => {
          const onReady = () => {
            video.removeEventListener('canplay', onReady)
            resolve()
          }
          video.addEventListener('canplay', onReady)
          if (video.readyState >= 3) resolve()
        })
        arm(video)
        await video.play().catch(() => {})
      }
    }

    // Standby buffer stays idle until the visible video has fully buffered,
    // so first paint never competes with a second download of the same file.
    let standbyArmed = false
    const armStandby = () => {
      if (standbyArmed) return
      standbyArmed = true
      try {
        vb.src = HERO_VIDEO_SRC
        vb.preload = 'auto'
        vb.load()
      } catch {
        /* ignore */
      }
    }

    const swapTo = async (current: HTMLVideoElement, next: HTMLVideoElement) => {
      const currentKey = current === va ? 'a' : 'b'
      if (swappingRef.current || frontRef.current !== currentKey) return
      if (!Number.isFinite(current.duration) || current.duration <= LOOP_LEAD_SEC * 2) return

      swappingRef.current = true
      try {
        // Pre-roll the hidden buffer so the visible layer never seeks
        next.pause()
        next.currentTime = 0
        await safePlay(next)
        await waitForFrame(next)

        const nextKey = currentKey === 'a' ? 'b' : 'a'
        frontRef.current = nextKey
        setFront(nextKey)
        setHasFrame(true)

        // Reset hidden buffer after paint — keeps next loop warm without a flash
        requestAnimationFrame(() => {
          current.pause()
          try {
            current.currentTime = 0
          } catch {
            /* ignore seek abort */
          }
          swappingRef.current = false
        })
      } catch {
        swappingRef.current = false
      }
    }

    let raf = 0
    let cancelled = false
    const tick = () => {
      if (cancelled) return
      const current = frontRef.current === 'a' ? va : vb
      const next = current === va ? vb : va
      const nearEnd =
        Number.isFinite(current.duration) &&
        current.duration > 0 &&
        !current.paused &&
        current.currentTime >= Math.max(0, current.duration - LOOP_LEAD_SEC)

      if (nearEnd && !swappingRef.current) {
        if (next.readyState >= 3) {
          void swapTo(current, next)
        } else if (current.currentTime >= current.duration - 0.05) {
          // Fallback: standby not buffered yet — restart in place rather than freeze
          armStandby()
          try {
            current.currentTime = 0
            void current.play().catch(() => {})
          } catch {
            /* ignore */
          }
        }
      }
      raf = requestAnimationFrame(tick)
    }

    const kickStart = async () => {
      arm(va)
      arm(vb)
      await safePlay(va)
      setHasFrame(true)
    }

    // Only start downloading the standby copy once A is fully buffered
    const onBuffered = () => armStandby()
    va.addEventListener('canplaythrough', onBuffered, { once: true })

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      const current = frontRef.current === 'a' ? va : vb
      if (current.paused) void safePlay(current)
    }

    arm(va)
    arm(vb)
    document.addEventListener('visibilitychange', onVisible)
    void kickStart()
    raf = requestAnimationFrame(tick)

    return () => {
      cancelled = true
      cancelAnimationFrame(raf)
      va.removeEventListener('canplaythrough', onBuffered)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const layerClass = 'absolute inset-0 h-full w-full object-cover object-center'

  return (
    <div className="absolute inset-0 bg-[#1a1a1a] isolate" aria-hidden="true">
      <img
        src={HERO_POSTER_SRC}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-center"
        fetchPriority="high"
        decoding="async"
      />
      {/* z-index swap only — never opacity-crossfade on loop (that reads as a stutter) */}
      <video
        ref={videoA}
        className={`${layerClass} ${hasFrame ? 'opacity-100' : 'opacity-0'} ${
          front === 'a' ? 'z-10' : 'z-0'
        }`}
        src={HERO_VIDEO_SRC}
        muted
        playsInline
        preload="auto"
        aria-hidden="true"
      />
      {/* Standby buffer: no src until the primary video finishes buffering */}
      <video
        ref={videoB}
        className={`${layerClass} ${hasFrame ? 'opacity-100' : 'opacity-0'} ${
          front === 'b' ? 'z-10' : 'z-0'
        }`}
        muted
        playsInline
        preload="none"
        aria-hidden="true"
      />
    </div>
  )
}

const Home: React.FC = () => {
  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  }

  const itemVariants: Variants = {
    hidden: { y: 30, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } }
  }

  const features = [
    {
      icon: CubeTransparentIcon,
      title: 'Blockchain Transparency',
      desc: 'Immutable records ensure complete transparency and traceability of all recycling data.'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Verified Compliance',
      desc: 'Automated compliance checking with real-time verification against regulatory standards.'
    },
    {
      icon: ChartBarIcon,
      title: 'Advanced Analytics',
      desc: 'Comprehensive reporting and analytics across all stakeholders in the recycling ecosystem.'
    }
  ]

  const stakeholders = [
    { icon: BuildingLibraryIcon, name: 'Government', desc: 'Regulatory oversight' },
    { icon: ArchiveBoxArrowDownIcon, name: 'Suppliers', desc: 'Data submission' },
    { icon: DocumentMagnifyingGlassIcon, name: 'Auditors', desc: 'Independent verification' },
    { icon: UserGroupIcon, name: 'Public', desc: 'Transparency access' }
  ]

  const stats = [
    { value: 140, suffix: 'M+', label: 'KG Plastic Recycled' },
    { value: 580, suffix: '+', label: 'Active Projects' },
    { value: 60623, suffix: '', label: 'Community Members' },
    { value: 99.9, suffix: '%', label: 'Chain Uptime', decimals: 1 }
  ]

  return (
    <div className="min-h-screen bg-white text-gray-700 font-sans overflow-x-hidden">
      {/* Liftable-style full-screen hero */}
      <section className="relative min-h-[92vh] flex flex-col overflow-hidden bg-black">
        <HeroLoopVideo />
        {/* Soft white wash so dark brand text stays readable over the video */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/55 via-white/25 to-white/60 pointer-events-none" aria-hidden="true" />

        {/* Header overlay — fully transparent */}
        <header className="relative z-20 bg-transparent border-b border-transparent">
          <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
            <div className="flex items-center min-w-0">
              <ESGLedgerLogo className="h-10 md:h-12" enlarged />
            </div>
            <nav className="hidden md:flex items-center gap-8 text-sm font-semibold text-primary-700">
              <a href="#features" className="hover:text-primary-500 transition-colors">Features</a>
              <a href="#stakeholders" className="hover:text-primary-500 transition-colors">Stakeholders</a>
              <a href="#map" className="hover:text-primary-500 transition-colors">Geo-Intel</a>
            </nav>
            <Link
              to="/login"
              className="inline-flex h-11 items-center justify-center rounded-lg bg-primary-500 px-6 text-sm font-bold text-white transition-all hover:bg-primary-700 hover:shadow-lg hover:shadow-primary-500/25"
            >
              Login
            </Link>
          </div>
        </header>

        {/* Centered hero copy */}
        <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-16 pt-8 text-center">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="max-w-6xl"
          >

            <motion.h1
              variants={itemVariants}
              className="flex justify-center"
            >
              <ESGLedgerLogo className="h-28 md:h-48 lg:h-64 drop-shadow-[0_2px_20px_rgba(255,255,255,0.85)]" />
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-4 text-2xl md:text-4xl font-bold text-gray-800 [text-shadow:0_2px_14px_rgba(255,255,255,0.9)]">
              built for sustainability.
            </motion.p>

            <motion.p variants={itemVariants} className="supplementary-type-dark mt-3 text-xs md:text-sm [text-shadow:0_1px_10px_rgba(255,255,255,0.95)]">
              powered by OneChain
            </motion.p>

            <motion.div variants={itemVariants}>
              <TypewriterHeadline />
            </motion.div>

            <motion.p variants={itemVariants} className="mt-8 max-w-6xl text-sm md:text-base text-gray-700 mx-auto leading-relaxed [text-shadow:0_1px_10px_rgba(255,255,255,0.9)] lg:whitespace-nowrap">
              Powered by blockchain, trusted by regulators, connected to verified environmental impact.
            </motion.p>

            <motion.div variants={itemVariants} className="mt-10 flex justify-center">
              <Link
                to="/login"
                className="inline-flex min-w-[200px] items-center justify-center rounded-lg bg-primary-500 px-8 py-4 text-sm font-bold tracking-wide text-white transition-all hover:bg-primary-600 hover:shadow-lg hover:shadow-primary-500/30"
              >
                Get Started
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Supplementary ticker strip */}
      <BrandTicker />

      {/* Impact stats */}
      <section className="relative py-16 md:py-20 px-6 md:px-12 max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, idx) => (
            <Reveal key={stat.label} delay={idx * 0.12}>
              <div className="text-3xl md:text-4xl font-extrabold text-primary-500 mb-2">
                <Counter target={stat.value} suffix={stat.suffix} decimals={stat.decimals ?? 0} />
              </div>
              <div className="supplementary-type">{stat.label}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CSDI Data Feature Section */}
      <section id="map" className="bg-primary-900 text-white py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#aededb 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-primary-500/20 blur-3xl animate-blob pointer-events-none" aria-hidden="true" />
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 md:gap-24 items-center">

            <Reveal>
              <div className="flex items-center gap-4 mb-8">
                <GlobeAsiaAustraliaIcon className="w-10 h-10 text-secondary-300" />
                <h3 className="supplementary-type-soft text-sm">Geo-Spatial Intelligence</h3>
              </div>
              <h2 className="font-display text-4xl md:text-6xl font-bold leading-none mb-8 text-white">
                HK Gov CSDI<br />
                <span className="text-secondary-300 font-normal text-3xl md:text-5xl">Topographic Data Integration</span>
              </h2>
              <p className="text-lg text-secondary-100/80 font-normal leading-relaxed mb-10 max-w-xl">
                ESGLedger natively integrates the Hong Kong Lands Department's Common Spatial Data Infrastructure (CSDI). By leveraging OpenLayers rendering, recycling routes, machine locations, and environmental impact zones are mapped with absolute governmental precision.
              </p>

              <div className="grid grid-cols-2 gap-8 border-t border-primary-700 pt-10">
                <div>
                  <div className="font-mono text-3xl font-bold text-secondary-200 mb-2">v10.8</div>
                  <div className="supplementary-type-soft text-xs">Openlayers Engine</div>
                </div>
                <div>
                  <div className="font-mono text-3xl font-bold text-secondary-200 mb-2">
                    <Counter target={100} suffix="%" />
                  </div>
                  <div className="supplementary-type-soft text-xs">CSDI API Compliant</div>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.15} className="relative group perspective-1000">
              <div className="absolute inset-0 bg-secondary-300/20 blur-3xl rounded-full transform group-hover:scale-110 transition-transform duration-1000" />
              <div className="relative bg-primary-800/60 border border-primary-700 p-2 rounded-xl transform rotate-y-[-10deg] rotate-x-[5deg] shadow-2xl transition-transform duration-700 hover:rotate-y-[0deg] hover:rotate-x-[0deg]">
                <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden relative border border-primary-700/50">
                  <CSDIHeroMap />

                  <div className="absolute inset-0 opacity-20 pointer-events-none" style={{
                    backgroundImage: 'linear-gradient(to right, #007771 1px, transparent 1px), linear-gradient(to bottom, #007771 1px, transparent 1px)',
                    backgroundSize: '20px 20px'
                  }} />
                  <div className="absolute inset-0 bg-gradient-to-tr from-gray-900 via-transparent to-primary-900/40 pointer-events-none z-10" />

                  <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-secondary-300/10 rounded-full blur-xl transform -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none" />

                  <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end z-10">
                    <div>
                      <div className="px-3 py-1 bg-primary-500 rounded text-xs font-bold tracking-widest uppercase mb-2 inline-flex items-center gap-2 text-white">
                        <span className="w-1.5 h-1.5 rounded-full bg-secondary-200 animate-glow-pulse" />
                        Live RVM Feed
                      </div>
                      <div className="font-display font-medium text-xl text-white">Central & Western District</div>
                    </div>
                    <CubeTransparentIcon className="w-8 h-8 text-secondary-300" />
                  </div>
                </div>
              </div>
            </Reveal>

          </div>
        </div>
      </section>

      {/* Why Choose ESGLedger */}
      <section id="features" className="py-24 md:py-32 bg-white px-6 md:px-12 relative overflow-hidden">
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-secondary-100/60 blur-3xl animate-blob-delayed pointer-events-none" aria-hidden="true" />
        <div className="max-w-[1400px] mx-auto relative">
          <Reveal className="mb-20 text-center">
            <p className="supplementary-type mb-4">Why Choose ESGLedger</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-primary-500 mb-6">Built for transparency,<br className="hidden md:block" /> designed for compliance</h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-12 gap-y-16">
            {features.map((feature, idx) => (
              <Reveal key={feature.title} delay={idx * 0.15}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                  className="group cursor-default relative p-8 rounded-2xl bg-white border border-secondary-200/60 hover:border-primary-300 hover:shadow-xl hover:shadow-primary-500/10 transition-all duration-300 h-full"
                >
                  <div className="w-16 h-16 bg-primary-500 flex items-center justify-center rounded-xl mb-8 group-hover:bg-primary-700 group-hover:scale-110 transition-all duration-500 shadow-lg shadow-primary-500/20">
                    <feature.icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-primary-600 mb-4">{feature.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{feature.desc}</p>
                  <div className="absolute bottom-0 left-0 w-0 h-[2px] bg-primary-500 group-hover:w-full transition-all duration-700 ease-out rounded-full" />
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Serving All Stakeholders */}
      <section id="stakeholders" className="py-24 md:py-32 bg-gradient-to-br from-primary-500 to-primary-700 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="max-w-[1400px] mx-auto relative">
          <Reveal className="mb-20 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">Serving All Stakeholders</h2>
            <p className="text-xl text-secondary-200 font-normal">A comprehensive system for every role in the recycling ecosystem</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {stakeholders.map((stakeholder, idx) => (
              <Reveal key={stakeholder.name} delay={idx * 0.1}>
                <motion.div
                  whileHover={{ y: -8, scale: 1.02 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 22 }}
                  className="bg-white p-8 rounded-2xl border border-white/40 hover:shadow-2xl hover:shadow-primary-900/30 transition-shadow duration-300 group h-full"
                >
                  <div className="w-14 h-14 bg-secondary-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-primary-500 transition-colors duration-300">
                    <stakeholder.icon className="w-7 h-7 text-primary-500 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-primary-600 mb-2">{stakeholder.name}</h3>
                  <p className="text-gray-500 text-sm">{stakeholder.desc}</p>
                </motion.div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="py-20 px-6 md:px-12 bg-white border-b border-secondary-200">
        <Reveal className="max-w-[900px] mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-primary-500 mb-6">Ready to make recycling verifiable?</h2>
          <p className="text-lg text-gray-600 mb-10">Join the network of regulators, suppliers, and auditors already using ESGLedger.</p>
          <motion.div whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.97 }} className="inline-block">
            <Link
              to="/login"
              className="inline-flex items-center gap-3 px-10 py-5 bg-primary-500 text-white rounded-lg text-sm font-bold tracking-widest uppercase hover:bg-primary-700 transition-colors duration-300 shadow-lg shadow-primary-500/25 group"
            >
              <span>Enter the Platform</span>
              <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </motion.div>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-gray-300 py-16 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 border-b border-gray-700 pb-16">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <ESGLedgerLogo className="h-16 md:h-20" enlarged variant="dark" />
            </div>
            <p className="text-sm max-w-sm mb-4 text-gray-300">Blockchain-powered recycling reporting system for transparency and accountability.</p>
          </div>
          <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase mb-6 text-white">System</div>
              <ul className="space-y-4 text-sm">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Documentation</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">API Reference</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">CSDI Setup</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs font-bold tracking-[0.18em] uppercase mb-6 text-white">Access</div>
              <ul className="space-y-4 text-sm">
                <li><Link to="/login" className="text-gray-400 hover:text-white transition-colors">Regulators</Link></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white transition-colors">Auditors</Link></li>
                <li><Link to="/login" className="text-gray-400 hover:text-white transition-colors">Retailers</Link></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto pt-8 flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm text-gray-400">© 2026 ESGLedger // Powered by OneChain Ltd.</div>
          <div className="mt-4 md:mt-0 flex gap-6">
            <span className="text-sm text-gray-400 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 animate-glow-pulse" /> System Status: Active
            </span>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
